import React, { useState, useEffect, useCallback } from "react";
import { Field, Input, MenuList, MenuItem, Spinner, makeStyles, tokens } from "@fluentui/react-components";
import Ows from "../../../utils/OwsHandler";
import _ from "lodash";

const useStyles = makeStyles({
  field: {
    position: "relative",
  },
  spinner: {
    position: "absolute",
    top: "32px",
    right: "8px",
  },
  menu: {
    position: "absolute",
    backgroundColor: tokens.colorNeutralBackground1,
    minHeight: "48px",
    maxWidth: "278px",
    maxHeight: "378px",
    width: "100%",
    boxShadow: `${tokens.shadow16}`,
    marginTop: "58px",
    paddingTop: "4px",
    paddingBottom: "4px",
    overflowY: "auto",
    zIndex: 1,
  },
});

const RelationField = ({ name, label, value, error, onChange }) => {
  const styles = useStyles();
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);

  const fetchRelations = useCallback(
    _.debounce(async (query) => {
      if (isSelecting) return;

      setLoading(true);

      const words = query.split(/\s+/).filter(Boolean);

      const searchConditions = words.map((word) => ({
        type: "OR",
        items: [
          {
            type: "COND",
            field: "relation",
            op: "LIKE",
            param: `%${word}%`,
          },
          {
            type: "COND",
            field: "customerNr",
            op: "LIKE",
            param: `%${word}%`,
          },
        ],
      }));

      const requestCondition = {
        type: "AND",
        items: [
          {
            type: "AND",
            items: searchConditions,
          },
        ],
      };

      const { response } = await Ows.handleRequest("Otys.Services.RelationService.getList", [
        {
          what: {
            uid: true,
            relation: true,
          },
          condition: requestCondition,
          sort: {
            relation: "ASC",
          },
          limit: 200,
        },
      ]);

      setLoading(false);

      const mappedOptions = response.map((relation) => ({
        key: relation.uid,
        value: relation.relation,
      }));
      
      if (!_.isEmpty(mappedOptions)) {
        setOptions(mappedOptions);
        setShowMenu(true);
      } else {
        setOptions([]);
        setShowMenu(false);
      }
    }, 500),
    [isSelecting]
  );

  useEffect(() => {
    if (inputValue && !isSelecting) {
      fetchRelations(inputValue);
    } else {
      setShowMenu(false);
    }
  }, [inputValue, isSelecting, fetchRelations]);

  const handleInputChange = (e) => {
    setIsSelecting(false);
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(name, newValue);

    if (newValue === "") {
      setShowMenu(false);
      setOptions([]);
    }
  };

  const handleItemSelect = (selectedName, selectedId) => {
    setIsSelecting(true);
    setInputValue(selectedName);
    setShowMenu(false);
    onChange(name, Number(selectedId));
  };

  return (
    <Field label={label} validationState={error ? "error" : "none"} validationMessage={error} className={styles.field}>
      <Input type="text" value={inputValue} onChange={handleInputChange} placeholder="Select a relation" />

      {loading && <Spinner size="extra-tiny" className={styles.spinner} />}

      {showMenu && (
        <MenuList className={styles.menu}>
          {options.map((option) => (
            <MenuItem key={option.key} onClick={() => handleItemSelect(option.value, option.key)}>
              {option.value}
            </MenuItem>
          ))}
        </MenuList>
      )}
    </Field>
  );
};

export default RelationField;
